import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';

import ButtonWithTooltip from 'js/components/common/ButtonWithTooltip';
import CruxSnackBar from 'js/components/common/CruxSnackBar';
import ValidationStatus from 'js/constants/validationStatus';
import propertyHubApi from 'js/api/propertyHubApi';
import { trackEvent } from 'js/actions/segment';
import { createMixpanelProperties } from 'js/helpers/Segment';
import Segment from 'js/constants/segment';
import Flags from 'js/constants/featureFlags';
import { isBankingAndFinanceSegment } from 'js/constants/industrySegments';
import { getAssetDomainOld } from 'js/constants/assets';
import { HISTORIC } from 'js/constants/propertyDetail';
import TOOLTIP from '../../../constants/tooltipText';

const StyledButtonWithTooltip = styled(ButtonWithTooltip)({
    marginLeft: '8px',
});

const LoadingOverlay = styled('div')({
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(254, 254, 254, 0.6)',
});

const isSafeUrl = (url) => {
    try {
        const { protocol } = new URL(url);
        return protocol === 'http:' || protocol === 'https:';
    } catch {
        return false;
    }
};

const PropertyHubButton = ({ propertyId, isHistoricProperty, isPropertyExist }) => {
    const dispatch = useDispatch();
    const [fetching, setFetching] = useState(false);
    const [errorSnackBar, setErrorSnackBar] = useState(false);
    const featureFlag = useSelector(state => state.launchDarkly.get('featureFlag'));
    const industrySegment = useSelector(state => state.clapi.get('usrDetail')?.accountDetail?.industrySegment);

    const isEnabled = featureFlag[Flags.PROPERTY_HUB_LINK];
    const isBankingAndFinance = isBankingAndFinanceSegment(industrySegment);

    if (!isEnabled || !isBankingAndFinance) {
        return null;
    }

    const handleClick = () => {
        setFetching(true);
        dispatch(trackEvent(createMixpanelProperties(Segment.EVENT_NAMES.ORDER_VALUATION, {
            page: Segment.EVENT_PROPERTIES.PROPERTY_DETAIL,
            propertyId,
        })));
        propertyHubApi.getLenderSelectionUrl(propertyId)
            .then((response) => {
                if (response.url && isSafeUrl(response.url)) {
                    window.open(response.url, '_blank', 'noopener,noreferrer');
                }
            })
            .catch(() => {
                setErrorSnackBar(true);
            })
            .finally(() => {
                setFetching(false);
            });
    };

    return (
        <>
            <StyledButtonWithTooltip
                color="secondary"
                className="property-hub-button"
                onClick={handleClick}
                tooltipMessage={isHistoricProperty ? HISTORIC.FEATURE_NOT_AVAILABLE : TOOLTIP.TEXT.MENU.UNAVAILABLE}
                tooltipPosition="bottom"
                isDisabled={isHistoricProperty || fetching || !isPropertyExist}
                displayTooltip={isHistoricProperty || !isPropertyExist}
                startIcon={
                    <img
                        style={{ width: '20px' }}
                        src={getAssetDomainOld('orderVal.png')}
                        alt="Order Valuation"
                    />
                }
            >
                <span>Order Valuation</span>
                {
                    fetching &&
                    <LoadingOverlay>
                        <CircularProgress
                            size={20}
                            thickness={5}
                        />
                    </LoadingOverlay>
                }
            </StyledButtonWithTooltip>
            <CruxSnackBar
                open={errorSnackBar}
                onClose={() => setErrorSnackBar(false)}
                severity={ValidationStatus.ERROR}
                message="Sorry, there was an error connecting. Please refresh the page to try again."
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                sx={{
                    zIndex: 9999,
                }}
            />
        </>
    );
};

PropertyHubButton.propTypes = {
    propertyId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    isHistoricProperty: PropTypes.bool,
    isPropertyExist: PropTypes.bool,
};

export default PropertyHubButton;
